import { 
  CreateSubscriptionServiceModel, 
  UpdateSubscriptionServiceModel, 
  SubscriptionServiceResponseModel,
  BaseOperationResult,
  SubscriptionPaginatedResult,
  PaginationParams,
  AuthTokens
} from '../types';
import { API_CONFIG } from '../config/api';
import { AuthenticatedApiService } from './authenticatedApiService';

class SubscriptionApiService extends AuthenticatedApiService {
  /**
   * Get paginated list of subscriptions
   */
  async getSubscriptions(params: PaginationParams = {}): Promise<SubscriptionPaginatedResult> {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page !== undefined) queryParams.append('pageNumber', params.page.toString());
      if (params.pageSize !== undefined) queryParams.append('pageSize', params.pageSize.toString());
      if (params.searchTerm) queryParams.append('searchTerm', params.searchTerm);
      if (params.sortBy) queryParams.append('sortBy', params.sortBy);
      if (params.sortDirection) queryParams.append('sortDirection', params.sortDirection);

      const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBSCRIPTIONS}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      
      const response = await this.makeAuthenticatedCall(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get subscriptions error:', error);
      throw error;
    }
  }

  /**
   * Get subscription by ID
   */
  async getSubscriptionById(id: string): Promise<BaseOperationResult<SubscriptionServiceResponseModel>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_BY_ID(id)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get subscription by ID error:', error);
      throw error;
    }
  }

  /**
   * Create new subscription
   */
  async createSubscription(subscription: CreateSubscriptionServiceModel): Promise<BaseOperationResult<SubscriptionServiceResponseModel>> {
    try {
      console.log('📋 Creating subscription with data:', subscription);
      console.log('📋 Tenant ID being sent:', subscription.tenantId);
      
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBSCRIPTIONS}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscription),
      });

      return await response.json();
    } catch (error) {
      console.error('Create subscription error:', error);
      throw error;
    }
  }

  /**
   * Update subscription
   */
  async updateSubscription(id: string, subscription: UpdateSubscriptionServiceModel): Promise<BaseOperationResult<SubscriptionServiceResponseModel>> {
    try {
      console.log('📋 Updating subscription with data:', subscription);
      console.log('📋 Tenant ID being sent:', subscription.tenantId);
      
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_BY_ID(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(subscription),
      });

      return await response.json();
    } catch (error) {
      console.error('Update subscription error:', error);
      throw error;
    }
  }

  /**
   * Delete subscription
   */
  async deleteSubscription(id: string): Promise<BaseOperationResult<boolean>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_BY_ID(id)}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Delete subscription error:', error);
      throw error;
    }
  }

  /**
   * Toggle subscription status (Active/Inactive)
   */
  async toggleSubscriptionStatus(id: string, isActive: boolean): Promise<BaseOperationResult<SubscriptionServiceResponseModel>> {
    try {
      return await this.updateSubscription(id, { status: isActive ? 0 : 1 });
    } catch (error) {
      console.error('Toggle subscription status error:', error);
      throw error;
    }
  }

  /**
   * Get modules for a subscription
   */
  async getSubscriptionModules(id: string): Promise<BaseOperationResult<any[]>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_MODULES(id)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return await response.json();
    } catch (error) {
      console.error('Get subscription modules error:', error);
      throw error;
    }
  }

  /**
   * Update modules for a subscription
   */
  async updateSubscriptionModules(
    id: string, 
    modules: {
      add?: Array<{ moduleId: string; isActive: boolean }>;
      update?: Array<{ moduleId: string; isActive: boolean }>;
      remove?: string[];
    }
  ): Promise<BaseOperationResult<any>> {
    try {
      const response = await this.makeAuthenticatedCall(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SUBSCRIPTION_MODULES(id)}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(modules),
      });

      return await response.json();
    } catch (error) {
      console.error('Update subscription modules error:', error);
      throw error;
    }
  }
}

// Create a singleton instance
const subscriptionApiService = new SubscriptionApiService();

// Export the service with token management
export const subscriptionService = {
  /**
   * Initialize the service with tokens and refresh callback
   */
  initialize(tokens: AuthTokens | null, onTokenRefresh: (tokens: AuthTokens) => void) {
    subscriptionApiService.setTokens(tokens, onTokenRefresh);
  },

  /**
   * Get paginated list of subscriptions
   */
  async getSubscriptions(params: PaginationParams = {}): Promise<SubscriptionPaginatedResult> {
    return await subscriptionApiService.getSubscriptions(params);
  },

  /**
   * Get subscription by ID
   */
  async getSubscriptionById(id: string): Promise<BaseOperationResult<SubscriptionServiceResponseModel>> {
    return await subscriptionApiService.getSubscriptionById(id);
  },

  /**
   * Create new subscription
   */
  async createSubscription(subscription: CreateSubscriptionServiceModel): Promise<BaseOperationResult<SubscriptionServiceResponseModel>> {
    return await subscriptionApiService.createSubscription(subscription);
  },

  /**
   * Update subscription
   */
  async updateSubscription(id: string, subscription: UpdateSubscriptionServiceModel): Promise<BaseOperationResult<SubscriptionServiceResponseModel>> {
    return await subscriptionApiService.updateSubscription(id, subscription);
  },

  /**
   * Delete subscription
   */
  async deleteSubscription(id: string): Promise<BaseOperationResult<boolean>> {
    return await subscriptionApiService.deleteSubscription(id);
  },

  /**
   * Toggle subscription status (Active/Inactive)
   */
  async toggleSubscriptionStatus(id: string, isActive: boolean): Promise<BaseOperationResult<SubscriptionServiceResponseModel>> {
    return await subscriptionApiService.toggleSubscriptionStatus(id, isActive);
  },

  /**
   * Get modules for a subscription
   */
  async getSubscriptionModules(id: string): Promise<BaseOperationResult<any[]>> {
    return await subscriptionApiService.getSubscriptionModules(id);
  },

  /**
   * Update modules for a subscription
   */
  async updateSubscriptionModules(
    id: string, 
    modules: {
      add?: Array<{ moduleId: string; isActive: boolean }>;
      update?: Array<{ moduleId: string; isActive: boolean }>;
      remove?: string[];
    }
  ): Promise<BaseOperationResult<any>> {
    return await subscriptionApiService.updateSubscriptionModules(id, modules);
  },

  /**
   * Check if service is authenticated
   */
  isAuthenticated(): boolean {
    return subscriptionApiService.isAuthenticated();
  }
};
